import type { CompanySummary, ReverseGeocode, SecretariaSummary } from '@obras/shared';
import { formatDate, formatDateTime, formatDays } from '@obras/shared';
import { Link, useBlocker, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronRight,
  CloudCheck,
  History,
  Lock,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useRef, useState, useEffect, type FormEvent } from 'react';

import { PageHeader } from '../components/AppShell.js';
import { DESLIZE, focarNaEtapa } from '../components/ui/Assistente.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { TrilhaDeEtapas, type EtapaDaTrilha } from '../components/ui/Etapas.js';
import { EmptyState, FormError, Notice } from '../components/ui/Feedback.js';
import { Modal } from '../components/ui/Modal.js';
import { api, ApiError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { CompanyFormModal } from '../features/empresas/CompanyFormModal.js';
import { useCreateObra } from '../features/obras/api.js';
import {
  coordOrNull,
  corpoDaObra,
  formularioInicial,
  useOpcoesDaObra,
  type FormularioDaObra,
} from '../features/obras/formularioDaObra.js';
import {
  EtapaCapa,
  EtapaContrato,
  EtapaFiscalizacao,
  EtapaIdentificacao,
  EtapaLocalizacao,
  EtapaPrazos,
  EtapaRevisao,
} from '../features/obras/nova/EtapasDaNovaObra.js';
import {
  CABECALHO_DA_ETAPA,
  ETAPA_DO_CAMPO,
  ETAPAS_DA_OBRA,
  errosDaEtapa,
  ROTULO_DA_ETAPA,
  type EtapaDaObraId,
} from '../features/obras/nova/etapas.js';
import {
  apagarRascunhoDaObra,
  chaveDoRascunhoDaObra,
  gravarRascunhoDaObra,
  lerRascunhoDaObra,
  miniaturaDaCapa,
  type CapaGuardada,
} from '../features/obras/rascunhoDaObra.js';
import { SecretariaFormModal } from '../features/secretarias/SecretariaFormModal.js';

const ICON = { strokeWidth: 1.5 } as const;

/**
 * Cadastro de obra, numa página própria e em etapas (design.md §Nova obra).
 *
 * Era um modal com dezoito campos num sumário. Virou uma página com a trilha à
 * esquerda e uma pergunta por etapa — identificação, contrato, prazos,
 * fiscalização, localização, capa — e a revisão no fim, de onde se cria. Quem
 * não alcança o cadastro vê o porquê, e não um formulário que o servidor
 * recusaria.
 */
export function NovaObraPage() {
  const { may } = useAuth();

  if (!may('canCreateObras')) {
    return (
      <>
        <PageHeader breadcrumb={<Trilha />} title="Nova obra" />
        <Card square>
          <EmptyState
            icon={Lock}
            title="Cadastro de obras indisponível"
            description="Seu acesso não inclui o cadastro de obras. A permissão é concedida pela gestão do órgão."
            action={
              <Link
                to="/obras"
                className="inline-flex h-10 items-center bg-ink/8 px-4 text-[13px] font-medium text-ink transition-colors hover:bg-ink/14"
              >
                Voltar às obras
              </Link>
            }
          />
        </Card>
      </>
    );
  }

  return <AssistenteDaNovaObra />;
}

function Trilha() {
  return (
    <span className="flex items-center gap-1.5">
      <Link to="/obras" className="transition-colors hover:text-ink">
        Obras
      </Link>
      <ChevronRight size={13} strokeWidth={1.75} aria-hidden />
      <span className="text-ink">Nova obra</span>
    </span>
  );
}

function AssistenteDaNovaObra() {
  const { user, organization, can, may } = useAuth();
  const navigate = useNavigate();
  const create = useCreateObra();
  const opcoes = useOpcoesDaObra();

  /*
   * Quem cadastra já vem como fiscal responsável — o fiscal, sempre, e o
   * gestor também, que pode designar outro servidor. Fiscal comum não troca:
   * apontar um colega é atribuir trabalho, decisão de gestão. Abaixo de fiscal
   * a obra nasce sem responsável (ADR 044).
   */
  const podeDesignar = can('gestor');
  const assumeResponsabilidade = !podeDesignar && can('fiscal');
  const intervaloPadrao = organization?.defaultVisitIntervalDays ?? 15;

  const [base] = useState<FormularioDaObra>(() =>
    formularioInicial(undefined, {
      fiscal: can('fiscal') ? user?.id : undefined,
      intervalo: intervaloPadrao,
    }),
  );

  /*
   * O rascunho é de quem cadastra, no órgão em que cadastra, e é lido uma vez,
   * na montagem.
   */
  const chave = chaveDoRascunhoDaObra(organization?.id ?? '-', user?.id ?? '-');
  const ultima = ETAPAS_DA_OBRA.length - 1;
  const [rascunho] = useState(() => lerRascunhoDaObra(chave, base, ultima));

  const [form, setForm] = useState<FormularioDaObra>(rascunho?.form ?? base);
  /** A capa à mostra (`blob:` logo depois do envio, `data:` vinda do rascunho). */
  const [capa, setCapa] = useState<CapaGuardada | null>(rascunho?.capa ?? null);
  /** A miniatura em `data:` que o rascunho guarda; pronta um instante depois do envio. */
  const [miniatura, setMiniatura] = useState<string | null>(rascunho?.capa?.url ?? null);
  /** Os auxiliares que entram com a obra, além do responsável. */
  const [equipe, setEquipe] = useState<string[]>(() => [...(rascunho?.equipe ?? [])]);
  const [indice, setIndice] = useState(rascunho?.indice ?? 0);
  const [alcancada, setAlcancada] = useState(
    rascunho ? Math.max(rascunho.alcancada, rascunho.indice) : 0,
  );
  const [direcao, setDirecao] = useState(1);
  /** Etapas em que "Continuar" já foi tentado: dali em diante, os erros aparecem. */
  const [tentou, setTentou] = useState<ReadonlySet<EtapaDaObraId>>(() => new Set());
  const [errosDoServidor, setErrosDoServidor] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [recuperadoEm, setRecuperadoEm] = useState<string | null>(rascunho?.salvoEm ?? null);
  const [buscandoEndereco, setBuscandoEndereco] = useState(false);
  const [novaEmpresa, setNovaEmpresa] = useState(false);
  const [novaSecretaria, setNovaSecretaria] = useState(false);
  /** A empresa recém-cadastrada, até a lista de opções voltar com ela. */
  const [empresaNova, setEmpresaNova] = useState<CompanySummary | null>(null);
  const [secretariaNova, setSecretariaNova] = useState<SecretariaSummary | null>(null);

  /** A etapa que entra leva o foco ao primeiro campo — inclusive a primeira, ao abrir. */
  const focarAoEntrar = useRef(true);
  /** Depois de criada (ou de sair confirmando), a navegação não pergunta mais nada. */
  const liberado = useRef(false);
  /** A capa cuja miniatura está sendo feita: uma troca rápida não pode gravar a anterior. */
  const capaEmPreparo = useRef('');
  const topoRef = useRef<HTMLElement>(null);

  const etapaId: EtapaDaObraId = ETAPAS_DA_OBRA[indice] ?? 'identificacao';
  const naRevisao = indice === ultima;
  const salvando = create.isPending;

  function definir<K extends keyof FormularioDaObra>(campo: K, valor: FormularioDaObra[K]): void {
    setForm((atual) => ({ ...atual, [campo]: valor }));
    // Responsável é vaga única: quem passa a responder sai da lista de auxiliares.
    if (campo === 'responsibleFiscalId') {
      setEquipe((atual) => atual.filter((id) => id !== valor));
    }
    // Mexer num campo tira dele a recusa do servidor: ela falava do valor antigo.
    const doServidor = campo === 'initialValue' ? 'initialValueCents' : campo;
    setErrosDoServidor((atual) => {
      if (!(doServidor in atual)) return atual;
      const resto = { ...atual };
      delete resto[doServidor];
      return resto;
    });
  }

  // ── o que se calcula do que foi preenchido

  const temErros = (id: EtapaDaObraId): boolean => Object.keys(errosDaEtapa(id, form)).length > 0;
  const temErroDoServidor = (id: EtapaDaObraId): boolean =>
    Object.keys(errosDoServidor).some((campo) => ETAPA_DO_CAMPO[campo] === id);
  const pendencias = ETAPAS_DA_OBRA.filter((id) => temErros(id));

  const errosVisiveis: Record<string, string> = {
    ...errosDoServidor,
    ...(tentou.has(etapaId) ? errosDaEtapa(etapaId, form) : {}),
  };
  const erroDe = (campo: string): { error?: string } =>
    errosVisiveis[campo] ? { error: errosVisiveis[campo] } : {};

  const empresaEscolhida =
    opcoes.empresasCompletas.find((empresa) => empresa.id === form.companyId) ??
    (empresaNova?.id === form.companyId ? empresaNova : null);
  const nomeDaSecretaria =
    opcoes.secretarias.find((opcao) => opcao.value === form.secretariaId)?.label ??
    (secretariaNova?.id === form.secretariaId ? secretariaNova.name : null);
  const nomeDoFiscal = podeDesignar
    ? (opcoes.fiscais.find((opcao) => opcao.value === form.responsibleFiscalId)?.label ?? null)
    : assumeResponsabilidade
      ? (user?.name ?? null)
      : null;

  /** Quem pode entrar na equipe: o órgão inteiro, menos quem já é o responsável. */
  const membrosDaEquipe = opcoes.membros
    .filter((membro) => membro.id !== form.responsibleFiscalId)
    .map((membro) => ({
      value: membro.id,
      label: membro.name,
      detail: membro.jobTitle ?? membro.email ?? undefined,
    }));
  const nomesDaEquipe = equipe.map(
    (id) => opcoes.membros.find((membro) => membro.id === id)?.name ?? 'Servidor do órgão',
  );

  // ── o rascunho

  /** Há o que perder? Qualquer coisa além do formulário em branco, uma capa ou uma equipe. */
  const preenchido =
    capa !== null || equipe.length > 0 || JSON.stringify(form) !== JSON.stringify(base);

  /*
   * O cadastro vai para o rascunho a cada mudança — e só a cada mudança: abrir
   * a página não regrava nada, e o horário do rascunho continua sendo o da
   * última vez em que alguém preencheu. A primeira passada só anota o que já
   * está em tela.
   */
  const guardado = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const conteudo = preenchido
      ? {
          form,
          capa: capa && miniatura ? { mediaId: capa.mediaId, url: miniatura } : null,
          equipe,
          indice,
          alcancada,
        }
      : null;
    const assinatura = conteudo ? JSON.stringify(conteudo) : null;

    if (guardado.current === undefined || assinatura === guardado.current) {
      guardado.current = assinatura;
      return;
    }
    guardado.current = assinatura;

    if (conteudo) {
      gravarRascunhoDaObra(chave, { versao: 1, salvoEm: new Date().toISOString(), ...conteudo });
    } else {
      apagarRascunhoDaObra(chave);
    }
  }, [chave, preenchido, form, capa, miniatura, equipe, indice, alcancada]);

  /*
   * Sair da página com algo preenchido pergunta antes — o menu, o "voltar" do
   * navegador, o "Cancelar". Recarregar não pergunta: o rascunho já está no
   * navegador e volta sozinho.
   */
  const bloqueio = useBlocker({
    shouldBlockFn: () => !liberado.current && preenchido,
    withResolver: true,
    enableBeforeUnload: false,
  });

  // ── navegação

  function irPara(destino: number): void {
    if (destino === indice || destino < 0 || destino > ultima) return;
    setDirecao(destino > indice ? 1 : -1);
    setIndice(destino);
    setAlcancada((atual) => Math.max(atual, destino));
    setErroGeral(null);
    setRecuperadoEm(null);
    focarAoEntrar.current = true;
  }

  /** Quando a etapa nova entra, o topo do cartão volta à vista se tinha saído dela. */
  function trazerAoTopo(): void {
    const topo = topoRef.current;
    if (!topo || topo.getBoundingClientRect().top >= 0) return;
    const reduzido = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    topo.scrollIntoView({ behavior: reduzido ? 'auto' : 'smooth', block: 'start' });
  }

  function focarPrimeiroErro(): void {
    requestAnimationFrame(() => {
      topoRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
    });
  }

  /** Marca a etapa como tentada, vai até ela e põe o foco no primeiro problema. */
  function mostrarPendencia(id: EtapaDaObraId): void {
    setTentou((atual) => new Set(atual).add(id));
    const destino = ETAPAS_DA_OBRA.indexOf(id);
    if (destino === indice) focarPrimeiroErro();
    else irPara(destino);
  }

  function avancar(): void {
    if (temErros(etapaId)) {
      mostrarPendencia(etapaId);
      return;
    }
    irPara(indice + 1);
  }

  /** Pela trilha, volta-se livre; para a frente, só passando por etapas completas. */
  function irPelaTrilha(destino: number): void {
    if (destino < indice) {
      irPara(destino);
      return;
    }
    const pendente = ETAPAS_DA_OBRA.slice(0, destino).find((id) => temErros(id));
    if (pendente) mostrarPendencia(pendente);
    else irPara(destino);
  }

  function enviarFormulario(evento: FormEvent): void {
    evento.preventDefault();
    // Enter num campo avança a etapa; criar é só pelo botão da revisão.
    if (!naRevisao) avancar();
  }

  // ── capa e endereço

  function escolherCapa(escolhida: { mediaId: string; url: string } | null): void {
    setCapa(escolhida);
    setMiniatura(null);
    definir('coverMediaId', escolhida?.mediaId ?? '');
    capaEmPreparo.current = escolhida?.mediaId ?? '';
    if (!escolhida) return;

    void miniaturaDaCapa(escolhida.url).then((dados) => {
      if (dados && capaEmPreparo.current === escolhida.mediaId) setMiniatura(dados);
    });
  }

  /**
   * Traz logradouro, município e UF do ponto marcado — só no que está em
   * branco: o endereço do projeto é mais preciso que o do OpenStreetMap, e quem
   * já o digitou não pode perdê-lo por ter ajustado o pino. Falha em silêncio:
   * é comodidade, e os campos continuam ali para preencher à mão.
   */
  async function marcar(lat: number, lng: number): Promise<void> {
    setForm((atual) => ({ ...atual, latitude: String(lat), longitude: String(lng) }));
    setBuscandoEndereco(true);
    try {
      const achado = await api.get<ReverseGeocode>(
        `/obras/endereco-por-coordenada?lat=${lat}&lng=${lng}`,
      );
      setForm((atual) => ({
        ...atual,
        address: atual.address || (achado.address ?? ''),
        city: atual.city || (achado.city ?? ''),
        state: atual.state || (achado.state ?? ''),
      }));
    } catch {
      // Silêncio proposital; ver o comentário acima.
    } finally {
      setBuscandoEndereco(false);
    }
  }

  // ── recomeçar, sair, criar

  /** "Descartar rascunho", no aviso: volta à primeira etapa, em branco. */
  function recomecar(): void {
    setForm(base);
    setCapa(null);
    setMiniatura(null);
    setEquipe([]);
    capaEmPreparo.current = '';
    setDirecao(-1);
    setIndice(0);
    setAlcancada(0);
    setTentou(new Set());
    setErrosDoServidor({});
    setErroGeral(null);
    setRecuperadoEm(null);
    focarAoEntrar.current = true;
  }

  function mostrarRecusa(error: unknown): void {
    if (error instanceof ApiError && error.details) {
      const campos = error.fieldErrors();
      setErrosDoServidor(campos);
      setErroGeral('Verifique os campos destacados.');
      // Salta para a primeira etapa com campo recusado.
      const primeira = ETAPAS_DA_OBRA.find((id) =>
        Object.keys(campos).some((campo) => ETAPA_DO_CAMPO[campo] === id),
      );
      if (primeira && primeira !== etapaId) irPara(ETAPAS_DA_OBRA.indexOf(primeira));
      return;
    }
    setErroGeral(error instanceof ApiError ? error.message : 'Não foi possível criar a obra.');
  }

  async function criar(): Promise<void> {
    const pendente = ETAPAS_DA_OBRA.find((id) => temErros(id));
    if (pendente) {
      mostrarPendencia(pendente);
      return;
    }

    setErroGeral(null);
    setErrosDoServidor({});

    try {
      const obra = await create.mutateAsync({
        ...corpoDaObra(form),
        teamMemberIds: equipe.filter((id) => id !== form.responsibleFiscalId),
      });
      // Criada, o rascunho cumpriu o papel — e a página sai sem perguntar.
      apagarRascunhoDaObra(chave);
      guardado.current = null;
      liberado.current = true;
      void navigate({ to: '/obras/$obraId', params: { obraId: obra.id } });
    } catch (error) {
      mostrarRecusa(error);
    }
  }

  // ── a trilha

  const lat = coordOrNull(form.latitude);
  const lng = coordOrNull(form.longitude);

  const resumoDa = (id: EtapaDaObraId, posicao: number): string | undefined => {
    const passou = posicao < alcancada;
    switch (id) {
      case 'identificacao':
        return form.name.trim() || undefined;
      case 'contrato':
        return form.contractNumber.trim() ? `Contrato ${form.contractNumber.trim()}` : undefined;
      case 'prazos':
        if (form.osEnd) return `OS até ${formatDate(form.osEnd)}`;
        if (form.contractEnd) return `Contrato até ${formatDate(form.contractEnd)}`;
        return passou ? 'Não informados' : undefined;
      case 'fiscalizacao': {
        // Responsável e intervalo já nascem preenchidos: só viram resumo depois de a pessoa passar por ali.
        if (posicao > alcancada) return undefined;
        const mais = equipe.length > 0 ? ` + ${equipe.length}` : '';
        if (nomeDoFiscal) return `${nomeDoFiscal}${mais}`;
        if (equipe.length > 0) {
          return equipe.length === 1 ? '1 pessoa na equipe' : `${equipe.length} pessoas na equipe`;
        }
        return form.visitIntervalDays.trim()
          ? `Vistoria a cada ${formatDays(Number(form.visitIntervalDays))}`
          : undefined;
      }
      case 'localizacao':
        if (form.city.trim()) {
          return [form.city.trim(), form.state.trim()].filter(Boolean).join('/');
        }
        if (lat !== null && lng !== null) return 'Ponto marcado no mapa';
        return passou ? 'Não informada' : undefined;
      case 'capa':
        if (capa) return 'Imagem escolhida';
        return passou ? 'Sem imagem' : undefined;
      default:
        return undefined;
    }
  };

  const trilha: EtapaDaTrilha[] = ETAPAS_DA_OBRA.map((id, posicao) => ({
    id,
    rotulo: ROTULO_DA_ETAPA[id],
    resumo: resumoDa(id, posicao),
    feita: posicao < alcancada && !temErros(id),
    // Pula-se adiante quando o que vem antes está em ordem: as etapas opcionais não seguram ninguém.
    alcancavel:
      posicao <= alcancada ||
      ETAPAS_DA_OBRA.slice(0, posicao).every((anterior) => !temErros(anterior)),
    comErro: temErroDoServidor(id) || (tentou.has(id) && temErros(id)),
  }));

  // ── a etapa à vista

  const propsDaEtapa = { form, definir, erroDe };

  const conteudo =
    etapaId === 'identificacao' ? (
      <EtapaIdentificacao
        {...propsDaEtapa}
        secretarias={opcoes.secretarias}
        carregandoSecretarias={opcoes.carregando.secretarias}
        podeCadastrarSecretaria={may('canAccessSecretarias')}
        onNovaSecretaria={() => setNovaSecretaria(true)}
      />
    ) : etapaId === 'contrato' ? (
      <EtapaContrato
        {...propsDaEtapa}
        empresas={opcoes.empresas}
        empresaEscolhida={empresaEscolhida}
        carregandoEmpresas={opcoes.carregando.empresas}
        podeCadastrarEmpresa={may('canManageCompanies')}
        onNovaEmpresa={() => setNovaEmpresa(true)}
      />
    ) : etapaId === 'prazos' ? (
      <EtapaPrazos {...propsDaEtapa} />
    ) : etapaId === 'fiscalizacao' ? (
      <EtapaFiscalizacao
        {...propsDaEtapa}
        fiscais={opcoes.fiscais}
        carregandoFiscais={opcoes.carregando.fiscais}
        podeDesignar={podeDesignar}
        fiscalFixo={nomeDoFiscal}
        membrosDaEquipe={membrosDaEquipe}
        equipe={equipe}
        onEquipe={setEquipe}
        notaDoFiscal={
          assumeResponsabilidade
            ? 'A obra será registrada sob sua responsabilidade. A transferência de titularidade compete à gestão.'
            : 'A designação do fiscal responsável compete à gestão.'
        }
        intervaloPadrao={intervaloPadrao}
      />
    ) : etapaId === 'localizacao' ? (
      <EtapaLocalizacao
        {...propsDaEtapa}
        buscandoEndereco={buscandoEndereco}
        onMarcar={(latitude, longitude) => void marcar(latitude, longitude)}
      />
    ) : etapaId === 'capa' ? (
      <EtapaCapa form={form} capa={capa} fiscal={nomeDoFiscal} onCapa={escolherCapa} />
    ) : (
      <EtapaRevisao
        form={form}
        capa={capa}
        nomes={{
          secretaria: nomeDaSecretaria,
          empresa: empresaEscolhida,
          fiscal: nomeDoFiscal,
          equipe: nomesDaEquipe,
        }}
        pendencias={pendencias}
        onAlterar={(id) => irPara(ETAPAS_DA_OBRA.indexOf(id))}
      />
    );

  const cabecalho = CABECALHO_DA_ETAPA[etapaId];

  return (
    <>
      <PageHeader
        breadcrumb={<Trilha />}
        title="Nova obra"
        description="O cadastro em etapas, com revisão no fim. Só a denominação, o número do contrato e o valor são obrigatórios; o resto pode ser completado depois."
      />

      <div className="grid gap-6 md:grid-cols-[232px_minmax(0,1fr)] md:items-start">
        <aside className="md:sticky md:top-6">
          <div className="border border-line bg-surface-2 px-5 py-5">
            <TrilhaDeEtapas
              rotulo="Etapas do cadastro"
              etapas={trilha}
              atual={indice}
              onIr={irPelaTrilha}
            />
          </div>
          <AnimatePresence initial={false}>
            {preenchido ? (
              <motion.p
                key="guardado"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="mt-3 hidden items-center gap-1.5 text-[12px] text-ink-3 md:flex"
              >
                <CloudCheck {...ICON} size={14} aria-hidden />
                Rascunho guardado neste navegador
              </motion.p>
            ) : null}
          </AnimatePresence>
        </aside>

        <section
          ref={topoRef}
          aria-label="Cadastro da obra"
          className="flex min-w-0 scroll-mt-6 flex-col border border-line bg-surface"
        >
          <form onSubmit={enviarFormulario} noValidate className="min-w-0">
            <AnimatePresence
              mode="wait"
              initial={false}
              custom={direcao}
              onExitComplete={trazerAoTopo}
            >
              <motion.div
                key={etapaId}
                data-etapa
                custom={direcao}
                variants={DESLIZE}
                initial="entra"
                animate="parado"
                exit="sai"
                ref={(elemento: HTMLDivElement | null) => {
                  if (!elemento || !focarAoEntrar.current) return;
                  focarAoEntrar.current = false;
                  focarNaEtapa(elemento);
                }}
                className="flex flex-col gap-6 px-5 py-6 sm:px-8 sm:py-7"
              >
                <header>
                  <p className="tabular text-[11px] font-medium tracking-[0.06em] text-ink-3 uppercase">
                    Etapa {indice + 1} de {ETAPAS_DA_OBRA.length}
                  </p>
                  <h2
                    data-titulo-da-etapa
                    tabIndex={-1}
                    className="mt-1 text-[20px] font-semibold tracking-tight outline-none"
                  >
                    {cabecalho.titulo}
                  </h2>
                  <p className="mt-1 max-w-[64ch] text-[14px] text-ink-3">{cabecalho.descricao}</p>
                </header>

                {recuperadoEm ? (
                  <Notice tone="primary" icon={History}>
                    <span className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                      <span>
                        <strong className="font-semibold">Rascunho recuperado</strong>, guardado em{' '}
                        {formatDateTime(recuperadoEm)}. O cadastro continua de onde parou.
                      </span>
                      <button
                        type="button"
                        onClick={recomecar}
                        className="text-[12px] font-medium underline underline-offset-2 transition-colors hover:text-primary"
                      >
                        Descartar rascunho
                      </button>
                    </span>
                  </Notice>
                ) : null}

                {erroGeral ? <FormError>{erroGeral}</FormError> : null}

                {conteudo}
              </motion.div>
            </AnimatePresence>
          </form>

          {/*
           * O rodapé gruda no pé da janela enquanto a etapa rola: em etapa longa
           * (o mapa, a revisão), "Continuar" não fica lá embaixo, fora de vista.
           */}
          <footer className="sticky bottom-0 z-10 flex flex-wrap items-center justify-end gap-2 border-t border-line bg-surface-2 px-5 py-4 sm:px-8">
            <span className="tabular mr-auto hidden text-[13px] text-ink-3 sm:inline">
              Etapa {indice + 1} de {ETAPAS_DA_OBRA.length}
            </span>
            {indice === 0 ? (
              <Button variant="ghost" onClick={() => void navigate({ to: '/obras' })}>
                Cancelar
              </Button>
            ) : (
              <Button
                variant="ghost"
                icon={<ArrowLeft {...ICON} size={16} />}
                onClick={() => irPara(indice - 1)}
                disabled={salvando}
              >
                Voltar
              </Button>
            )}
            {naRevisao ? (
              <Button
                variant="primary"
                loading={salvando}
                icon={<Check {...ICON} size={16} />}
                onClick={() => void criar()}
              >
                Criar obra
              </Button>
            ) : (
              <Button variant="primary" icon={<ArrowRight {...ICON} size={16} />} onClick={avancar}>
                Continuar
              </Button>
            )}
          </footer>
        </section>
      </div>

      {/*
       * Cadastrar empresa e secretaria daqui, sem sair: a recém-criada já volta
       * escolhida.
       */}
      <CompanyFormModal
        open={novaEmpresa}
        onClose={() => setNovaEmpresa(false)}
        onSaved={(empresa: CompanySummary) => {
          setEmpresaNova(empresa);
          definir('companyId', empresa.id);
        }}
      />
      <SecretariaFormModal
        open={novaSecretaria}
        onClose={() => setNovaSecretaria(false)}
        onSaved={(criada: SecretariaSummary) => {
          setSecretariaNova(criada);
          definir('secretariaId', criada.id);
        }}
      />

      {/*
       * A confirmação de saída: o roteador segurou a navegação, e ela só segue
       * pela escolha daqui. `Esc` e o clique fora voltam ao preenchimento.
       */}
      <Modal
        open={bloqueio.status === 'blocked'}
        onClose={() => bloqueio.reset?.()}
        title="Sair sem criar a obra?"
        focoInicial="[data-foco-inicial]"
        footer={
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center">
            <Button
              variant="danger"
              className="sm:mr-auto"
              onClick={() => {
                apagarRascunhoDaObra(chave);
                guardado.current = null;
                liberado.current = true;
                bloqueio.proceed?.();
              }}
            >
              Descartar rascunho
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                liberado.current = true;
                bloqueio.proceed?.();
              }}
            >
              Sair e guardar
            </Button>
            <Button variant="primary" data-foco-inicial onClick={() => bloqueio.reset?.()}>
              Continuar preenchendo
            </Button>
          </div>
        }
      >
        <p className="text-[14px] text-ink-2">
          O que foi preenchido fica guardado como rascunho neste navegador e volta quando "Nova
          obra" for aberta de novo.
        </p>
      </Modal>
    </>
  );
}
